import { useEffect } from "react";

/**
 * useSeo — imperatively set document title, meta description, canonical URL,
 * Open Graph tags and an optional JSON-LD block for the current route.
 * Cleans up on unmount by restoring the previous values.
 */
export default function useSeo({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  keywords,
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const ensure = (selector, create) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return { el, created };
    };

    const setMeta = (name, content, attr = "name") => {
      if (!content) return null;
      const { el, created } = ensure(
        `meta[${attr}="${name}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute(attr, name);
          return m;
        },
      );
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, created, prev };
    };

    const canonEntry = canonical
      ? ensure('link[rel="canonical"]', () => {
          const l = document.createElement("link");
          l.setAttribute("rel", "canonical");
          return l;
        })
      : null;
    let canonPrev = null;
    if (canonEntry) {
      canonPrev = canonEntry.el.getAttribute("href");
      canonEntry.el.setAttribute("href", canonical);
    }

    const metas = [
      setMeta("description", description),
      setMeta("keywords", (keywords || []).join(", ")),
      setMeta("og:title", title, "property"),
      setMeta("og:description", description, "property"),
      setMeta("og:image", ogImage, "property"),
      setMeta("og:url", canonical, "property"),
      setMeta("og:type", ogType, "property"),
      setMeta("twitter:card", "summary_large_image"),
      setMeta("twitter:title", title),
      setMeta("twitter:description", description),
      setMeta("twitter:image", ogImage),
    ].filter(Boolean);

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(jsonLd);
      jsonLdEl.setAttribute("data-seo-jsonld", "1");
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      metas.forEach((m) => {
        if (!m) return;
        if (m.created) m.el.remove();
        else if (m.prev != null) m.el.setAttribute("content", m.prev);
      });
      if (canonEntry) {
        if (canonEntry.created) canonEntry.el.remove();
        else if (canonPrev != null) canonEntry.el.setAttribute("href", canonPrev);
      }
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, ogType, JSON.stringify(keywords || []), JSON.stringify(jsonLd || null)]);
}
